import { Checkbox } from '@lbcamden/lib-react';
import { FunctionComponent, ReactNode, useMemo } from 'react';

import { ConditionalFormInput, ConditionalFormInputProps } from '../ConditionalFormInput';
import { CheckboxContainer } from './ConditionalCheckboxGroup.styled';
import { CustomOption } from './CustomOption';

export type CheckboxOption = {
  id: string;
  label: ReactNode;
} & ({ value?: string } | { isCustom: true });

export interface ConditionalCheckboxGroupProps extends Omit<
  ConditionalFormInputProps<string[]>,
  'initialYesValue'
> {
  /** Rendered after the checkbox options */
  nestedContent?: ReactNode;
  options: string[] | CheckboxOption[];
  errorOnEmptyCustomValues?: boolean;
}

const buildOptionsFromStrings = (strOpts: string[]) =>
  strOpts.map<CheckboxOption>((value) => ({
    id: value,
    label: value,
    value,
  }));

export const ConditionalCheckboxGroup: FunctionComponent<ConditionalCheckboxGroupProps> = ({
  nestedContent,
  options,
  errorOnEmptyCustomValues,
  ...props
}) => {
  const { value, disabled, onChange } = props;

  const optionList = useMemo(
    () =>
      (typeof options[0] === 'string'
        ? buildOptionsFromStrings(options as string[])
        : options) as CheckboxOption[],
    [options],
  );

  const isTrue = Array.isArray(value);

  const onOptionSelected = (optValue: string) => {
    if (!isTrue) {
      return;
    }
    const newValue = value.includes(optValue)
      ? value.toSpliced(value.indexOf(optValue), 1)
      : [...value, optValue];

    onChange(newValue);
  };

  return (
    <ConditionalFormInput {...props} initialYesValue={[]}>
      {isTrue && (
        <>
          {optionList.map(({ id: optId, label, ...option }) =>
            'isCustom' in option ? (
              <CustomOption
                key={optId}
                id={optId}
                label={label}
                value={value}
                disabled={disabled}
                errorOnEmpty={errorOnEmptyCustomValues}
                onChange={onChange}
              />
            ) : (
              <CheckboxContainer key={optId}>
                <Checkbox
                  id={optId}
                  checked={isTrue && value.includes(option.value ?? optId)}
                  disabled={disabled}
                  onChange={() => onOptionSelected(option.value ?? optId)}
                />
                <label htmlFor={optId}>{label}</label>
              </CheckboxContainer>
            ),
          )}
          {nestedContent}
        </>
      )}
    </ConditionalFormInput>
  );
};
